import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Inject,
  Input,
  NgZone,
  OnChanges,
  Output,
  SimpleChanges
} from '@angular/core';
import {Observable, of} from 'rxjs';
import {debounceTime, distinctUntilChanged, switchMap} from 'rxjs/operators';
import {NgbTypeaheadSelectItemEvent} from '@ng-bootstrap/ng-bootstrap';
import {Address} from '../../../../addresses/models/address.model';

type PlacePrediction = google.maps.places.AutocompletePrediction;

const SEARCH_DEBOUNCE_MS = 300;
const PLACE_TYPES = ['geocode'];
const PLACE_DETAILS_FIELDS = ['address_components', 'geometry', 'formatted_address', 'place_id'];

require('./address-autocomplete-input.scss');
@Component({
  standalone: false,
  selector: 'app-address-autocomplete-input',
  templateUrl: './address-autocomplete-input.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AddressAutocompleteInputComponent implements OnChanges {

  @Input() address: Address;
  @Input() placeholder: string = 'Search Address';

  @Output() onChange = new EventEmitter<Address | undefined>();

  public searchText: string | PlacePrediction = '';

  // Place details come from the Places API (same as predictions), the Geocoding API is not enabled for every key.
  private autocompleteService = new google.maps.places.AutocompleteService();
  private placesService = new google.maps.places.PlacesService(document.createElement('div'));

  constructor(@Inject(ChangeDetectorRef) private cdr: ChangeDetectorRef,
              @Inject(NgZone) private ngZone: NgZone,
              @Inject('analyticsService') private analyticsService) {
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.address) {
      this.searchText = this.address?.place || '';
    }
  }

  public search = (text$: Observable<string>): Observable<PlacePrediction[]> => text$.pipe(
    debounceTime(SEARCH_DEBOUNCE_MS),
    distinctUntilChanged(),
    switchMap(term => term?.trim() ? this._getPlacePredictions(term) : of([]))
  );

  public formatPrediction = (prediction: string | PlacePrediction): string => {
    return typeof prediction === 'string' ? prediction : prediction?.description || '';
  };

  onSearchTextChange(value: string | PlacePrediction) {
    this.searchText = value;
    if (typeof value !== 'string') {
      return;
    }
    this.onChange.emit(value.trim() ? {place: value} : undefined);
  }

  onSelectItem(event: NgbTypeaheadSelectItemEvent<PlacePrediction>) {
    const placeId = event.item?.place_id;
    if (!placeId) {
      return;
    }
    this.placesService.getDetails({placeId, fields: PLACE_DETAILS_FIELDS}, (place, status) => {
      this.ngZone.run(() => {
        this.analyticsService.trackEvent('PlaceDetails', 'addressAutocompleteInput', status);
        if (status !== google.maps.places.PlacesServiceStatus.OK || !place?.geometry?.location) {
          return;
        }
        this.onChange.emit(this._placeToAddress(place));
        this.cdr.markForCheck();
      });
    });
  }

  private _getPlacePredictions(input: string): Observable<PlacePrediction[]> {
    return new Observable<PlacePrediction[]>(observer => {
      this.autocompleteService.getPlacePredictions({input, types: PLACE_TYPES}, (predictions, status) => {
        this.ngZone.run(() => {
          observer.next(status === google.maps.places.PlacesServiceStatus.OK ? predictions : []);
          observer.complete();
        });
      });
    });
  }

  private _placeToAddress(place: google.maps.places.PlaceResult): Address {
    const getTypeValue = (type: string): string | undefined => {
      return place.address_components?.find(component => component.types.includes(type))?.long_name;
    };

    return {
      house: getTypeValue('street_number'),
      neighborhood: getTypeValue('neighborhood'),
      street: getTypeValue('route') || getTypeValue('neighborhood'),
      city: getTypeValue('locality') || getTypeValue('sublocality'),
      country: getTypeValue('country'),
      state: getTypeValue('administrative_area_level_1'),
      zipCode: getTypeValue('postal_code'),
      location: {
        latitude: place.geometry.location.lat(),
        longitude: place.geometry.location.lng()
      },
      place: place.formatted_address,
      placeId: place.place_id
    };
  }
}
